// eslint-disable-next-line no-restricted-imports
import { getAuthPayload } from './helpers'
import { getCredentials, removeCredentials, setCredentials } from './storage'
import type { AuthTokenPayload, Credentials, CredentialsResponse } from './types'
import { getConfig } from '@/tools/config'
import { fetcher, removeAuthorization, setAuthorization } from '@/tools/fetcher'

interface TwoFactorResponse {
  twoFactorToken: string
  expiresIn: number
}

interface AuthTokenResponse {
  access: Credentials | null
  twoFactor: TwoFactorResponse | null
}

export const initAuth = () => {
  const credentials = getCredentials()
  if (credentials) {
    setAuthorization({ accessToken: credentials.accessToken }, refresh, signOut)
  }
}
const isApiError = (e: unknown): e is { response?: { status: number } } =>
  typeof e === 'object' &&
  !!e &&
  (!('response' in e) || ('response' in e && typeof e.response === 'object'))

export const signOut = async (): Promise<void> => {
  const { apiUrl } = getConfig()
  const credentials = getCredentials()
  if (!credentials) {
    return
  }
  removeCredentials()
  try {
    await fetcher.post(`${apiUrl}/oauth/revoke`, { token: credentials.refreshToken })
  } catch (e: unknown) {
    if (isApiError(e) && e.response?.status !== 200) {
      throw e
    }
  } finally {
    removeAuthorization()
  }
}
export const refresh = async (): Promise<Credentials> => {
  const { apiUrl } = getConfig()
  const credentials = getCredentials()
  if (!credentials) {
    throw new Error('No credentials found')
  }
  const response = await fetcher.post<Credentials>(`${apiUrl}/oauth/refresh`, {
    refreshToken: credentials?.refreshToken,
  })
  const newCredentials = response.data
  setCredentials(newCredentials)
  return newCredentials
}

export const signIn = async (
  payload: AuthTokenPayload,
  config: { headers: Record<string, string> } = { headers: {} }
) => {
  const authPayload = getAuthPayload(payload)
  const { apiUrl } = getConfig()

  const response = await fetcher.post<AuthTokenResponse>(`${apiUrl}/oauth/token`, authPayload, {
    headers: { Authorization: null, ...config.headers },
  })

  const credentials = response.data

  if (credentials.access) {
    setCredentialsAndAuthorization(credentials.access)
  }

  if (credentials.twoFactor) {
    return credentials.twoFactor
  }
}

export const setCredentialsAndAuthorization = (credentials: Credentials) => {
  setCredentials(credentials)
  setAuthorization({ accessToken: credentials.accessToken }, refresh, signOut)
}

export const getAuthenticationMethods = async (email: string) => {
  const { apiUrl } = getConfig()
  const response = await fetcher.get<CredentialsResponse>(
    `${apiUrl}/v3/customers/auth/${email}/methods`
  )
  const { authenticationMethods } = response.data.data
  if (!authenticationMethods.length) {
    return null
  }
  return authenticationMethods
}

export const validateTwoFactor = async (twoFactorToken: string, verificationCode: string) => {
  const { apiUrl } = getConfig()
  const response = await fetcher.post<Credentials>(
    `${apiUrl}/oauth/2fa/phone_verification/validate`,
    { twoFactorToken, code: verificationCode },
    { headers: { Authorization: null } }
  )
  const credentials = response.data
  setCredentialsAndAuthorization(credentials)
}

export { getCredentials, setCredentials, removeCredentials } from './storage'

export type { GrantType, AuthenticationMethods, AuthTokenPayload, Credentials } from './types'
